import { type Easing, motion } from "framer-motion"
import { memo, useId } from "react"
import { tokenRoundelConfig } from "../data/token-roundel-data"

interface Props {
  inline: boolean
}

export const TokenRoundelContent = memo(function TokenRoundelContent({ inline }: Props) {
  const config = inline ? tokenRoundelConfig.inline : tokenRoundelConfig.portal
  const { animation, assets } = tokenRoundelConfig
  const pathId = useId()
  const gradientId = useId()

  return (
    <>
      {/* Token image with subtle bobbing and fade-in */}
      <motion.img
        src={assets.tokenImage}
        alt="$BRACKY token"
        className="w-full h-full object-contain select-none"
        initial={{ opacity: 0, scale: 0.64 }}
        animate={{
          opacity: 1,
          scale: 0.68,
          y: animation.tokenBobbing.values,
        }}
        transition={{
          duration: 0.26,
          ease: "easeOut",
          y: {
            duration: animation.tokenBobbing.duration,
            ease: animation.tokenBobbing.ease as Easing,
            repeat: Number.POSITIVE_INFINITY,
            repeatType: "reverse",
          },
        }}
        whileTap={{ scale: 0.62 }}
      />

      {/* Rotating text ring – PNG only for inline mode */}
      {inline && (
        <motion.img
          src={assets.rotatingRing}
          alt="Rotating $BRACKY ring-3"
          className="absolute inset-0 w-full h-full object-contain select-none opacity-90 pointer-events-none"
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{
            repeat: Number.POSITIVE_INFINITY,
            ease: animation.ringRotation.ease as Easing,
            duration: animation.ringRotation.duration,
          }}
          aria-hidden="true"
        />
      )}

      {/* SVG text ring for non-inline (portal) mode */}
      {!inline && (
        <svg
          viewBox="0 0 100 100"
          className="absolute inset-0 w-full h-full overflow-visible pointer-events-none rotate-text"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            <path id={pathId} d="M50 50 m -45 0 a 45 45 0 1 1 90 0 a 45 45 0 1 1 -90 0" />
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="400%" y2="0%" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="#0074FF" />
              <stop offset="20%" stopColor="#0074FF" />
              <stop offset="35%" stopColor="#1A88FF" />
              <stop offset="50%" stopColor="#30DDFF" />
              <stop offset="65%" stopColor="#1A88FF" />
              <stop offset="80%" stopColor="#0074FF" />
              <stop offset="100%" stopColor="#0074FF" />
              <animateTransform
                attributeName="gradientTransform"
                type="translate"
                values="-200,0; 200,0"
                dur={`${animation.shimmerGradient.duration}s`}
                repeatCount="indefinite"
              />
            </linearGradient>
          </defs>
          <text
            className="font-mono"
            style={{
              fontSize: config.fontSize,
              letterSpacing: config.letterSpacing,
              wordSpacing: config.wordSpacing,
              fill: `url(#${gradientId})`,
            }}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            <textPath href={`#${pathId}`} startOffset="50%">
              {Array.from(config.ringText).map((char, idx) =>
                char === "/" ? (
                  <tspan
                    // biome-ignore lint/suspicious/noArrayIndexKey: Characters repeat in ring text - index needed to distinguish duplicates
                    key={`char-${char}-${idx}`}
                    style={{ fill: "#0074FF", opacity: 0.66 }}
                  >
                    /
                  </tspan>
                ) : (
                  <tspan
                    // biome-ignore lint/suspicious/noArrayIndexKey: Characters repeat in ring text - index needed to distinguish duplicates
                    key={`char-${char}-${idx}`}
                  >
                    {char}
                  </tspan>
                ),
              )}
            </textPath>
          </text>
        </svg>
      )}
    </>
  )
})
